/**
 * PayrollCard — Individual payroll draft card with salary breakdown.
 * Extracted from Payroll.jsx for maintainability.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar, Download, CheckCircle, Send, Info, Loader2, AlertTriangle } from 'lucide-react';
import { ChildIcon, genderBg, genderColor, genderAccent } from '../ChildIcon';

export const PayrollCard = ({
  draft,
  existingDecl,
  isDeclared,
  isParent,
  downloadingPdf,
  downloadingCra,
  FileCheckIcon,
  onDownloadPdf,
  onDownloadCra,
  onDeclare,
  getStatusBadge,
}) => {
  const pajemploiReady = draft.pajemploi_ready;
  const pajemploiMissing = draft.pajemploi_missing || [];
  const canDeclare = isParent && !isDeclared && pajemploiReady;
  const cantDeclareReason = !pajemploiReady && pajemploiMissing.length > 0;

  return (
    <Card
      className="overflow-hidden transition-shadow duration-200 hover:shadow-md"
      data-testid={`payroll-card-${draft.contract_id}-${draft.month}`}
    >
      <CardHeader className="pb-3" style={{ backgroundColor: genderBg(draft.child_sex) }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: genderAccent(draft.child_sex) }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: genderBg(draft.child_sex), border: `1.5px solid ${genderColor(draft.child_sex)}` }}>
                <ChildIcon sex={draft.child_sex} className="h-4 w-4" style={{ color: genderColor(draft.child_sex) }} />
              </div>
              {draft.child_first_name || draft.child_name}
            </CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(draft.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {existingDecl && getStatusBadge(existingDecl.status)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* Salary Summary Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 rounded-lg bg-[var(--neutral-50)]">
            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Heures</p>
            <p className="text-lg font-bold" style={{ color: 'var(--primary-600)' }}>{draft.hours_worked}h</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-[var(--neutral-50)]">
            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Jours</p>
            <p className="text-lg font-bold" style={{ color: 'var(--primary-600)' }}>{draft.days_worked}j</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-[var(--neutral-50)]">
            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Brut</p>
            <p className="text-lg font-bold" style={{ color: 'var(--primary-600)' }}>{draft.gross_salary.toFixed(2)}€</p>
          </div>
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--success-light)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Net à payer</p>
            <p className="text-lg font-bold" style={{ color: 'var(--success)' }}>{draft.net_salary.toFixed(2)}€</p>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium flex items-center gap-1 py-2" style={{ color: 'var(--primary-500)' }}>
            <Info className="h-4 w-4" /> Voir le détail
          </summary>
          <div className="mt-2 space-y-3 text-sm">
            <Separator />
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Indemnités</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <span style={{ color: 'var(--text-secondary)' }}>Repas</span>
                <span className="text-right font-medium">{draft.meal_allowance.toFixed(2)}€</span>
                <span style={{ color: 'var(--text-secondary)' }}>Goûter</span>
                <span className="text-right font-medium">{draft.snack_allowance.toFixed(2)}€</span>
                <span style={{ color: 'var(--text-secondary)' }}>Entretien</span>
                <span className="text-right font-medium">{draft.daily_maintenance_allowance.toFixed(2)}€</span>
                {draft.km_allowance > 0 && (<>
                  <span style={{ color: 'var(--text-secondary)' }}>Kilométrique</span>
                  <span className="text-right font-medium">{draft.km_allowance.toFixed(2)}€</span>
                </>)}
                <span className="font-semibold" style={{ color: 'var(--primary-600)' }}>Total indemnités</span>
                <span className="text-right font-bold" style={{ color: 'var(--primary-600)' }}>{draft.total_allowances.toFixed(2)}€</span>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span style={{ color: 'var(--text-secondary)' }}>Cotisations salariales</span>
              <span className="text-right font-medium text-[var(--secondary-500)]">-{draft.employee_contributions.toFixed(2)}€</span>
              <span style={{ color: 'var(--text-secondary)' }}>Cotisations patronales</span>
              <span className="text-right font-medium text-[var(--secondary-500)]">{draft.employer_contributions.toFixed(2)}€</span>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span style={{ color: 'var(--text-secondary)' }}>Taux horaire</span>
              <span className="text-right font-medium">{draft.hourly_rate.toFixed(2)}€/h</span>
            </div>
          </div>
        </details>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mt-4 pt-4 border-t border-[var(--neutral-200)]">
          <Button variant="outline" size="sm" className="gap-2"
            onClick={() => onDownloadPdf(draft.contract_id, draft.month, draft.child_name)}
            disabled={downloadingPdf === `${draft.contract_id}-${draft.month}`}
            data-testid={`download-pdf-${draft.contract_id}-${draft.month}`}
          >
            {downloadingPdf === `${draft.contract_id}-${draft.month}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Bulletin PDF
          </Button>
          <Button variant="outline" size="sm" className="gap-2"
            onClick={() => onDownloadCra(draft.contract_id, draft.month, draft.child_name)}
            disabled={downloadingCra === `${draft.contract_id}-${draft.month}`}
            data-testid={`download-cra-${draft.contract_id}-${draft.month}`}
          >
            {downloadingCra === `${draft.contract_id}-${draft.month}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheckIcon className="h-4 w-4" />}
            CRA PDF
          </Button>
          {isParent && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="col-span-2 sm:col-span-1">
                    <Button size="sm" className="gap-2 w-full"
                      onClick={() => canDeclare && onDeclare(draft)}
                      disabled={isDeclared || !pajemploiReady}
                      style={canDeclare ? { backgroundColor: 'var(--primary-400)', color: 'white' } : {}}
                      data-testid={`declare-btn-${draft.contract_id}-${draft.month}`}
                    >
                      {isDeclared ? (
                        <><CheckCircle className="h-4 w-4" /> Déjà déclarée</>
                      ) : !pajemploiReady ? (
                        <><AlertTriangle className="h-4 w-4" /> Infos manquantes</>
                      ) : (
                        <><Send className="h-4 w-4" /> Déclarer Pajemploi</>
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {cantDeclareReason && (
                  <TooltipContent side="top" className="max-w-xs text-left" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--neutral-200)' }}>
                    <p className="font-semibold text-xs mb-1" style={{ color: 'var(--text-primary)' }}>Informations manquantes :</p>
                    <ul className="list-disc list-inside text-xs space-y-0.5" style={{ color: 'var(--text-secondary)' }}>
                      {pajemploiMissing.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
